/**
 * SkeletonLoader — YouTube-style shimmer placeholders for every tab.
 *
 * Each named export mirrors the real tab's layout so the page looks
 * "half loaded" instead of showing a spinner.  When real data arrives
 * it snaps into place seamlessly.
 */

import React from 'react';
import './SkeletonLoader.css';

/* ── Primitives ── */
const Pulse = ({ className = '', style = {} }) => (
    <div className={`skeleton-pulse ${className}`} style={style} />
);

/* ────────────────────────────────────────────────────────────────
   DASHBOARD / OVERVIEW SKELETON
   Header + tip + search bar + status tabs + 6 card grid
   ──────────────────────────────────────────────────────────────── */
export const DashboardSkeleton = () => (
    <div className="skeleton-container">
        {/* Header row: tip + title */}
        <div className="skeleton-dashboard-header">
            <div className="skeleton-tip">
                <Pulse className="skeleton-circle" style={{ width: 32, height: 32 }} />
                <div style={{ flex: 1 }}>
                    <Pulse className="skeleton-text short" />
                    <Pulse className="skeleton-text long" />
                </div>
            </div>
            <div className="skeleton-header-right">
                <Pulse className="skeleton-text title" style={{ width: 160 }} />
                <Pulse className="skeleton-text subtitle" style={{ width: 220 }} />
            </div>
        </div>

        {/* Search + sort */}
        <div className="skeleton-search-sort">
            <Pulse className="skeleton-search-input" />
            <Pulse className="skeleton-sort-control" />
            <Pulse className="skeleton-sort-btn" />
        </div>

        {/* Status tab pills */}
        <div className="skeleton-status-tabs">
            {[90, 80, 85, 75, 70, 105, 80, 85].map((w, i) => (
                <Pulse key={i} className="skeleton-tab-pill" style={{ width: w }} />
            ))}
        </div>

        {/* Card grid (6 cards) */}
        <div className="skeleton-card-grid">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton-card">
                    <Pulse className="skeleton-card-cover skeleton-rect" />
                    <div className="skeleton-card-body">
                        <Pulse className="skeleton-text title" />
                        <div className="skeleton-card-stats">
                            <Pulse className="skeleton-text short" style={{ width: 70 }} />
                            <Pulse className="skeleton-text short" style={{ width: 80 }} />
                            <Pulse className="skeleton-text short" style={{ width: 50 }} />
                        </div>
                        <div className="skeleton-card-time">
                            <Pulse className="skeleton-time-block" />
                            <Pulse className="skeleton-time-block" />
                            <Pulse className="skeleton-time-block" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

/* ────────────────────────────────────────────────────────────────
   COLLECTIONS / GROUPS SKELETON
   Header + search + accordion rows
   ──────────────────────────────────────────────────────────────── */
export const CollectionsSkeleton = () => (
    <div className="skeleton-container">
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
            <Pulse className="skeleton-text title" style={{ width: 200 }} />
            <Pulse className="skeleton-text subtitle" style={{ width: 280 }} />
            <div className="skeleton-header-buttons">
                <Pulse className="skeleton-btn" style={{ width: 180 }} />
                <Pulse className="skeleton-btn" style={{ width: 80 }} />
                <Pulse className="skeleton-btn" style={{ width: 80 }} />
            </div>
        </div>

        {/* Search + sort */}
        <div className="skeleton-search-sort">
            <Pulse className="skeleton-search-input" />
            <Pulse className="skeleton-sort-control" />
            <Pulse className="skeleton-sort-btn" />
        </div>

        {/* Group rows (8 rows) */}
        {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="skeleton-group-row">
                <Pulse className="skeleton-group-cover skeleton-rect" />
                <div className="skeleton-group-info">
                    <Pulse className="skeleton-text title" style={{ width: `${55 + (i % 3) * 10}%` }} />
                    <div className="skeleton-group-stats-row">
                        <Pulse className="skeleton-text" style={{ width: 70, height: 12 }} />
                        <Pulse className="skeleton-text" style={{ width: 90, height: 12 }} />
                        <Pulse className="skeleton-text" style={{ width: 60, height: 12 }} />
                    </div>
                    <div className="skeleton-group-time-row">
                        <Pulse className="skeleton-time-block" style={{ height: 32 }} />
                        <Pulse className="skeleton-time-block" style={{ height: 32 }} />
                        <Pulse className="skeleton-time-block" style={{ height: 32 }} />
                    </div>
                </div>
            </div>
        ))}
    </div>
);

/* ────────────────────────────────────────────────────────────────
   CARD-GRID SKELETON (Marathon / QuickWins)
   Header + ranked card list
   ──────────────────────────────────────────────────────────────── */
export const CardGridSkeleton = ({ title = '', subtitle = '' }) => (
    <div className="skeleton-container">
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
            <Pulse className="skeleton-text title" style={{ width: Math.min(title.length * 12, 300) || 200 }} />
            <Pulse className="skeleton-text subtitle" style={{ width: Math.min((subtitle || '').length * 7, 360) || 280 }} />
            <Pulse className="skeleton-text short" style={{ width: 180, marginTop: 8 }} />
        </div>

        {/* Ranked cards (8 cards) */}
        {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="skeleton-ranked-card">
                <Pulse className="skeleton-rank" />
                <Pulse className="skeleton-ranked-cover skeleton-rect" />
                <div className="skeleton-ranked-info">
                    <Pulse className="skeleton-text title" style={{ width: `${50 + (i % 4) * 10}%` }} />
                    <div className="skeleton-group-stats-row">
                        <Pulse className="skeleton-text" style={{ width: 75, height: 12 }} />
                        <Pulse className="skeleton-text" style={{ width: 90, height: 12 }} />
                        <Pulse className="skeleton-text" style={{ width: 55, height: 12 }} />
                    </div>
                    <div className="skeleton-group-time-row">
                        <Pulse className="skeleton-time-block" style={{ height: 32 }} />
                        <Pulse className="skeleton-time-block" style={{ height: 32 }} />
                        <Pulse className="skeleton-time-block" style={{ height: 32 }} />
                    </div>
                </div>
            </div>
        ))}
    </div>
);

/* ────────────────────────────────────────────────────────────────
   STATS SKELETON
   Header + summary cards + per-tab stat rows
   ──────────────────────────────────────────────────────────────── */
export const StatsSkeleton = () => (
    <div className="skeleton-container">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <Pulse className="skeleton-text title" style={{ width: 160 }} />
            <Pulse className="skeleton-rect" style={{ width: 160, height: 36, borderRadius: 8 }} />
        </div>

        {/* OP/ED note */}
        <Pulse className="skeleton-rect" style={{ width: '100%', height: 32, marginBottom: '1.5rem', borderRadius: 8 }} />

        {/* Summary cards grid */}
        <div className="skeleton-stats-summary">
            {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="skeleton-summary-card">
                    <Pulse className="skeleton-summary-icon" />
                    <div className="skeleton-summary-data">
                        <Pulse className="skeleton-text" style={{ width: '60%', height: 22 }} />
                        <Pulse className="skeleton-text" style={{ width: '80%', height: 12 }} />
                    </div>
                </div>
            ))}
        </div>

        {/* Section title */}
        <Pulse className="skeleton-text title" style={{ width: 200, marginBottom: '1rem' }} />

        {/* Stat rows */}
        {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skeleton-stat-row">
                <div className="skeleton-stat-identity">
                    <Pulse className="skeleton-circle" style={{ width: 32, height: 32 }} />
                    <div style={{ flex: 1 }}>
                        <Pulse className="skeleton-text" style={{ width: '70%', height: 14 }} />
                        <Pulse className="skeleton-text" style={{ width: '50%', height: 10 }} />
                    </div>
                </div>
                <div className="skeleton-stat-time-grid">
                    <Pulse className="skeleton-stat-time-cell" />
                    <Pulse className="skeleton-stat-time-cell" />
                    <Pulse className="skeleton-stat-time-cell" />
                </div>
            </div>
        ))}
    </div>
);

/* ────────────────────────────────────────────────────────────────
   RELATED SKELETON
   Tab buttons + card grid
   ──────────────────────────────────────────────────────────────── */
export const RelatedSkeleton = () => (
    <div className="skeleton-container">
        {/* Tab navigation */}
        <div className="skeleton-related-tabs">
            <Pulse className="skeleton-related-tab-btn" />
            <Pulse className="skeleton-related-tab-btn" />
        </div>

        {/* Card grid (6 cards) */}
        <div className="skeleton-card-grid">
            {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton-card">
                    <Pulse className="skeleton-card-cover skeleton-rect" />
                    <div className="skeleton-card-body">
                        <Pulse className="skeleton-text title" />
                        <Pulse className="skeleton-text medium" />
                        <div className="skeleton-card-stats">
                            <Pulse className="skeleton-text short" style={{ width: 70 }} />
                            <Pulse className="skeleton-text short" style={{ width: 80 }} />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

/* ────────────────────────────────────────────────────────────────
   COMPARE SKELETON
   Search input + saved comparisons
   ──────────────────────────────────────────────────────────────── */
export const CompareSkeleton = () => (
    <div className="skeleton-container">
        {/* Header */}
        <Pulse className="skeleton-text title" style={{ width: 200, marginBottom: '1rem' }} />

        {/* Search input area */}
        <div className="skeleton-compare-input-area">
            <Pulse className="skeleton-compare-input" />
            <Pulse className="skeleton-compare-btn" />
        </div>

        {/* Saved comparisons */}
        <Pulse className="skeleton-text subtitle" style={{ width: 180, marginBottom: '0.75rem' }} />
        {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="skeleton-saved-row">
                <Pulse className="skeleton-circle" style={{ width: 40, height: 40 }} />
                <div style={{ flex: 1 }}>
                    <Pulse className="skeleton-text" style={{ width: '40%', height: 14 }} />
                    <Pulse className="skeleton-text" style={{ width: '60%', height: 10 }} />
                </div>
            </div>
        ))}
    </div>
);
